import { Flex, Text, useDisclosure } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoColorBadge } from '~/components/shared/AutoColorBadge'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { MangoAlert } from '~/components/shared/MangoAlert'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { useColorMode } from '~/components/ui/color-mode'
import { useChannelSwitcher } from '~/hooks/useChannelSwitcher'
import { DropdownWrapper } from '~/pages/Settings/Shared'
import { NotificationDisposition } from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'

import { RestartConfirmationModal } from './RestartConfirmationModal'

export const ChannelSwitcher = () => {
  const { t } = useTranslation()
  const { colorMode } = useColorMode()
  const {
    currentChannel,
    availableChannels,
    isLoading,
    error,
    downloadStage,
    downloadProgress,
    switchChannelWithUpdate,
    validateChannel,
  } = useChannelSwitcher()

  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [pendingChannel, setPendingChannel] = useState<string>('')
  const [isSwitching, setIsSwitching] = useState<boolean>(false)

  const selectedChannelConfig = useMemo(
    () => availableChannels.find((c) => c.name === selectedChannel),
    [availableChannels, selectedChannel],
  )
  const isChangingChannel = selectedChannel !== currentChannel
  const channelRisk = selectedChannelConfig?.risk
  const showHighRiskWarning = channelRisk === 'high' && isChangingChannel
  const showMediumRiskWarning = channelRisk === 'medium' && isChangingChannel

  const { open: isOpen, onOpen, onClose } = useDisclosure()

  const {
    open: isErrorOpen,
    onOpen: onErrorOpen,
    onClose: onErrorClose,
  } = useDisclosure()
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleSwitchChannel = async () => {
    if (!selectedChannel || selectedChannel === currentChannel) return

    // Validate channel exists before proceeding
    const validation = await validateChannel(selectedChannel)
    if (!validation.exists) {
      const channelDisplayName =
        availableChannels.find((c) => c.name === selectedChannel)
          ?.displayName || selectedChannel
      setErrorMessage(
        t('updateSettings.channelSwitcher.validationError', {
          channelName: channelDisplayName,
        }),
      )
      onErrorOpen()
      return
    }

    setPendingChannel(selectedChannel)
    window.electron?.send('block-updates')
    onOpen()
  }

  const handleConfirmRestart = async () => {
    try {
      setIsSwitching(true)
      await switchChannelWithUpdate(pendingChannel)
      // App will restart automatically after download
    } catch (err) {
      console.error('Channel switch failed:', err)
      setIsSwitching(false)
      window.electron?.send('unblock-updates')
      onClose()
      // Show error modal with the error message
      setErrorMessage(
        err instanceof Error
          ? err.message
          : t(
              'updateSettings.channelSwitcher.switchFailed',
              'Failed to switch channel. Please try again.',
            ),
      )
      onErrorOpen()
    }
  }

  const handleCancelRestart = () => {
    window.electron?.send('unblock-updates')
    onClose()
    setPendingChannel('')
    setSelectedChannel('')
  }

  if (!isElectron) return null

  return (
    <>
      <Flex
        flexDirection={'column'}
        alignItems={'flex-start'}
        justifyContent={'left'}
        marginBottom={2}
        paddingX={2}
        width={'100%'}
      >
        <Flex
          flexDirection={'row'}
          alignItems={'flex-start'}
          marginBottom={3}
          gap={2}
        >
          {currentChannel && (
            <AutoColorBadge
              label={t('updateSettings.channelSwitcher.current', {
                channelName:
                  availableChannels.find((c) => c.name === currentChannel)
                    ?.displayName || currentChannel,
              })}
              colorKey={currentChannel}
              theme={colorMode}
              textTransform="capitalize"
              py={1}
              px={3}
            />
          )}
        </Flex>
        <Flex
          flexDirection={'column'}
          alignItems={'flex-start'}
          gap={3}
          width={'100%'}
        >
          <DropdownWrapper>
            <DropdownSelect
              items={availableChannels.map((channel) => ({
                value: channel.name,
                content: channel.displayName,
              }))}
              selectedValue={selectedChannel || currentChannel || ''}
              onSelect={(value) => setSelectedChannel(value as string)}
            />
          </DropdownWrapper>
          {selectedChannel && selectedChannel !== currentChannel && (
            <Flex alignItems="center" gap={2}>
              <Text textStyle="Body2">
                {t('updateSettings.channelSwitcher.switchTo')}
              </Text>
              <AutoColorBadge
                label={selectedChannelConfig?.displayName || selectedChannel}
                colorKey={selectedChannel}
                theme={colorMode}
              />
            </Flex>
          )}
          {showHighRiskWarning && (
            <MangoAlert
              status={NotificationDisposition.Error}
              title={t('updateSettings.channelSwitcher.highRiskWarning.title')}
              description={t(
                'updateSettings.channelSwitcher.highRiskWarning.description',
              )}
              width={'100%'}
              maxWidth={'100%'}
            />
          )}
          {showMediumRiskWarning && (
            <MangoAlert
              status={NotificationDisposition.Warning}
              title={t(
                'updateSettings.channelSwitcher.mediumRiskWarning.title',
              )}
              description={t(
                'updateSettings.channelSwitcher.mediumRiskWarning.description',
              )}
              width={'100%'}
              maxWidth={'100%'}
            />
          )}
          <MangoButton
            marginTop={2}
            size={Size.sm}
            variant={MangoButtonVariant.Default_300}
            onClick={handleSwitchChannel}
            isLoading={isLoading}
            disabled={
              isLoading ||
              !selectedChannel ||
              selectedChannel === currentChannel
            }
          >
            {t('updateSettings.channelSwitcher.switchChannel')}
          </MangoButton>
        </Flex>

        {error && (
          <Text
            fontSize={'xs'}
            color={'error'}
            textAlign={'center'}
            marginTop={2}
          >
            {error}
          </Text>
        )}
      </Flex>

      <RestartConfirmationModal
        isOpen={isOpen}
        onConfirm={handleConfirmRestart}
        onCancel={handleCancelRestart}
        channelName={
          availableChannels.find((c) => c.name === pendingChannel)
            ?.displayName || pendingChannel
        }
        isLoading={isSwitching}
        downloadStage={downloadStage}
        downloadProgress={downloadProgress}
      />

      <MangoModal
        isOpen={isErrorOpen}
        onClose={onErrorClose}
        header={t('updateSettings.channelSwitcher.error')}
        body={errorMessage}
        footer={
          <MangoButton
            variant={MangoButtonVariant.Default_300}
            onClick={onErrorClose}
          >
            {t('deleteConfirmModal.cancelButton')}
          </MangoButton>
        }
      />
    </>
  )
}
